import { url as urlHelper } from '@/helpers';
import { AxiosResponse } from 'axios';
import { fetchWrap, offlineResponse } from '../_helpers';

export interface Role {
    id: string;
    name: string;
    permissions: string[];
    isSystem?: boolean;
}

export interface Permission {
    name: string;
    description: string;
    group: string;
}

export interface PermissionGroup {
    name: string;
    permissions: Permission[];
}

export default {
    /**
     * Get all roles
     */
    async getAll(): Promise<Role[] | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap('/api/roles');
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return response.data;
        } else {
            return response;
        }
    },

    /**
     * Get a role by ID
     */
    async getById(id: string): Promise<Role | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap('/api/roles/' + id);
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return response.data;
        } else {
            return response;
        }
    },

    /**
     * Create a new role
     */
    async createRole(payload: { roleName: string }): Promise<Role | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap('/api/roles', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return response.data;
        } else {
            return response;
        }
    },

    /**
     * Create a new permission
     */
    async createPermission(payload: {
        id: number;
        name: string;
        description: string;
    }): Promise<Permission | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap('/api/roles/permissions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return response.data;
        } else {
            return response;
        }
    },

    /**
     * Add a permission to a role
     */
    async addPermissionToRole(payload: { roleName: string; permissionName: string }): Promise<boolean | AxiosResponse> {
        const urlRole = encodeURIComponent(payload.roleName);
        const urlPerm = encodeURIComponent(payload.permissionName);
        let response: AxiosResponse;
        try {
            response = await fetchWrap(`/api/roles/${urlRole}/permissions/${urlPerm}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return true;
        } else {
            return response;
        }
    },

    /**
     * Update an existing role
     */
    async updateRole(id: string | number, payload: { id: number; roleName: string }): Promise<Role | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap(`/api/roles/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return response.data;
        } else {
            return response;
        }
    },

    /**
     * Assign multiple permissions to a role
     */
    async assignPermissions(
        payload: { roleName: string; permissionName: string; enabled: boolean }[]
    ): Promise<boolean | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap('/api/roles/assign-permission', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return true;
        } else {
            return response;
        }
    },

    /**
     * Get role permissions
     */
    async getRolePermissions(roleName: string): Promise<string[] | AxiosResponse> {
        const urlRole = encodeURIComponent(roleName);
        let response: AxiosResponse;
        try {
            response = await fetchWrap(`/api/roles/${urlRole}/permissions`, {
                method: 'GET',
            });
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return response.data;
        } else {
            return response;
        }
    },

    /**
     * Update a role
     */
    async update(model: Partial<Role>): Promise<Role | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap('/api/roles/' + model.id, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(model),
            });
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return response.data;
        } else {
            return response;
        }
    },

    /**
     * Delete a role
     */
    async delete(id: string): Promise<boolean | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap('/api/roles/' + id, { method: 'DELETE' });
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return true;
        } else {
            return response;
        }
    },

    /**
     * Get all available permissions
     */
    async getPermissions(): Promise<PermissionGroup[] | AxiosResponse> {
        let response: AxiosResponse;
        try {
            response = await fetchWrap('/api/roles/permissions');
        } catch {
            response = offlineResponse();
        }
        if (response?.status >= 200 && response?.status < 300) {
            return response.data;
        } else {
            return response;
        }
    },
};
